import React, { useState } from 'react';
import { Button, ButtonProps } from '../../mui-builder/Button/Button';
import Loading from '../../mui-builder/Loading/Loading';
import { useAuthorization } from 'framework/authorization';
import { Modal } from 'framework/ui/ui-builder';
import { Breakpoint } from '@mui/system';
import { getVisibility, type SubmitProps, useSubmit } from 'framework/utils';
import useFieldHelper from '../../field/fieldHelper';
import { useUiTheme } from 'framework/uiTheme';
import { Typography } from '@mui/material';

export interface ButtonWithConfirmationProps extends ButtonProps, SubmitProps {
  displayMessage: string | any[];
  permissionToCheck?: Nullable<string | string[]>;
  children?: React.ReactNode;
  modalConfirmButtonClassName?: string;
  modalConfirmButtonText?: React.ReactNode;
  onToggleCallback?: () => void;
  modalChildren?: React.ReactElement | React.ReactElement[];
  confirmBtnProps?: ButtonProps;
  modalSize?: Breakpoint | false;
  closeOnSubmit?: boolean;
  disabledInput?: boolean;
  buttonTag?: React.ElementType;
  inputLabel?: string;
  isVisible?: boolean | ((data: any) => boolean);
}

const ButtonWithConfirmation = ({
  submitMethod,
  afterSubmit = {},
  children,
  displayMessage,
  permissionToCheck,
  modalConfirmButtonClassName,
  modalConfirmButtonText,
  color = 'primary',
  buttonTag,
  onToggleCallback = () => {},
  modalChildren,
  confirmBtnProps,
  modalSize,
  closeOnSubmit = true,
  isVisible = true,
  ...props
}: ButtonWithConfirmationProps) => {
  const [modalOpen, setModalOpen] = useState(false);
  const { isGranted } = useAuthorization();
  const { Button: ThemedButton = Button, Loading: ThemedLoading = Loading } = useUiTheme();
  const ButtonTag = buttonTag ?? ThemedButton;

  const { onSubmit, isLoading } = useSubmit({
    submitMethod,
    afterSubmit,
  });

  const { getData } = useFieldHelper();
  const data = getData();

  const isButtonVisible = getVisibility(data, isVisible);

  if (!isButtonVisible) {
    return null;
  }

  const confirmBtn = (
    <ThemedButton
      key="modal-confirm-button"
      className={modalConfirmButtonClassName}
      onClick={async () => {
        if (closeOnSubmit) {
          onSubmit();
        } else {
          await onSubmit();
        }

        setModalOpen(false);
      }}
      {...(confirmBtnProps || {})}
    >
      {modalConfirmButtonText || children}
    </ThemedButton>
  );

  return !permissionToCheck || isGranted(permissionToCheck) ? (
    <>
      <ButtonTag {...props} color={color} onClick={() => setModalOpen(true)}>
        {children}
      </ButtonTag>
      <Modal
        isOpen={modalOpen}
        title="Are you sure?"
        buttons={[confirmBtn]}
        onToggle={() => {
          if (closeOnSubmit || !isLoading) {
            setModalOpen(false);
            onToggleCallback();
          }
        }}
        modalSize={modalSize}
      >
        <>
          <Typography variant="body1" color="textPrimary" mb={1}>
            {displayMessage}
          </Typography>

          {modalChildren}

          {!closeOnSubmit && isLoading && <ThemedLoading hasBackground />}
        </>
      </Modal>
    </>
  ) : null;
};

export default ButtonWithConfirmation;
